import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button, Header, Message, Dimmer, Loader,
} from 'semantic-ui-react';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import { CSVLink } from 'react-csv';
import ContactList from '../views/AuctionContacts/ContactList';
import locationContactQuery from '../graphql/locationContactQuery';
import useGraphQLQuery from '../hooks/useGraphQLQuery';
import LoadingDataEnhancer from './LoadingData';
import AuctionContactForm, {
  AuctionLocationContactAction,
} from '../views/AuctionContacts/AuctionContactForm/AuctionContactForm';
import createOrUpdateLocationContactMutation from '../graphql/updateLocationContactQuery';
import { LocationContactAction } from '../views/AuctionContacts/ContactListRow';
import {
  auctionContactCSV,
  auctionContactHeader,
  exportFileName,
} from '../views/AuctionContacts/AuctionContactsExporter';
import { hasRole } from '../utilities/user';

export const messageSuccess = {
  Create: 'Successfully Created New Auction Location Contact',
  Update: 'Successfully Updated Auction Location Contact',
  Delete: 'Successfully Deleted Auction Location Contact',
};

const AuctionContacts = props => {
  const {
    match: { params: { locationId: paramLocationId } },
    createOrUpdateLocationContact,
    user,
    history,
  } = props;
  const isAuctionLocationEditor = hasRole('auction_location_editor', 'roles')(user);
  const [locationId, setLocationId] = useState(+paramLocationId || null);
  const [locationName, setLocationName] = useState('');
  const [contacts, setContacts] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null); // State to store error message
  const {
    data: contactsData,
    loading: contactLoading,
    error,
    refetch,
  } = useGraphQLQuery({
    query: locationContactQuery,
    variables: { locationId },
    skip: !locationId,
  });

  const [loading, setLoading] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');
  const [isNegativeFlashMsg, setIsNegativeFlashMsg] = useState(false);
  const [modalError, setModalError] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [contactData, setContactData] = useState(null);

  const [csvData, setCsvData] = useState([]);
  const [csvReady, setCsvReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const csvLinkRef = useRef();

  const handleCreateNewContact = () => {
    setShowLocationModal(true);
  };

  const onCloseHandler = () => {
    setShowLocationModal(false);
    setModalError('');
    setFlashMessage(''); // clear the error for the next open
    setSuccessMessage(false); // reset success flag
    setContactData(null);
  };

  const onClearError = useCallback(() => {
    setModalError('');
  }, []);

  const getLocationContactActionInput = useCallback((values, action) => {
    const {
      locationId: locId,
      contactId,
      contactName,
      title,
      contactEmail,
      phone,
      contactType,
      guaranteeConfirm,
      guaranteeCancel,
      returns,
      vehNotCovered,
    } = values;

    const locationContact = {
      locationId: locId,
      contactName,
      title,
      contactEmail,
      phone,
      contactType,
      guaranteeConfirm,
      guaranteeCancel,
      returns,
      vehNotCovered,
    };

    if (action === AuctionLocationContactAction.Update) {
      locationContact.contactId = contactId;
    }

    return { locationContact };
  }, []);

  const onFailedSubmit = useCallback((err, msg) => {
    setLoading(false);
    let errorMsg;

    if (msg !== undefined) {
      errorMsg = msg;
    } else if (Array.isArray(err)) {
      errorMsg = err.join(', ');
    } else {
      errorMsg = err.message;
    }

    // Show error in modal, not as flash message
    setModalError(errorMsg);
  }, []);

  const onExcuteMutationLocationContact = useCallback(
    async(mutation, mutationResultName, values, action) => {
      try {
        const input = getLocationContactActionInput(values, action);
        const response = await mutation({
          variables: { ...input },
        });

        const { success, errors, msg } = response.data[mutationResultName];

        if (success) {
          // Close modal and show success
          setShowLocationModal(false);
          setModalError('');
          setLoading(false);
          setSuccessMessage(true);
          setFlashMessage(messageSuccess[action]);
          setIsNegativeFlashMsg(false);

          // Refetch data
          refetch();
        } else {
          onFailedSubmit(errors, msg);
        }
      } catch (err) {
        onFailedSubmit(err, undefined); // Add undefined as second parameter
      }
    },
    [refetch, onFailedSubmit, getLocationContactActionInput]
  );

  const onFormSubmitHandler = useCallback(
    async values => {
      try {
        setLoading(true);
        setModalError(''); // Clear any previous modal errors
        const { action } = values;

        switch (action) {
          case AuctionLocationContactAction.Create:
          case AuctionLocationContactAction.Update:
            await onExcuteMutationLocationContact(
              createOrUpdateLocationContact,
              'createOrUpdateLocationContact',
              values,
              action
            );
            break;
          case AuctionLocationContactAction.Delete:
            break;
          default:
            break;
        }
      } catch (err) {
        onFailedSubmit(err, undefined); // Add undefined as second parameter
      }
    },
    [
      createOrUpdateLocationContact,
      onExcuteMutationLocationContact,
      onFailedSubmit,
    ]
  );

  const handleSelectContact = (contact, type) => {
    // TODO: Implement contact action
    switch (type) {
      case LocationContactAction.EDIT:
        setContactData(contact);
        setShowLocationModal(true);
        break;
      default:
        // No action for other types
        break;
    }
  };

  const processedData = useCallback(data => {
    const result = [];
    if (data) {
      data.forEach(loc => {
        if (loc.auctionName) {
          setLocationName(loc.auctionName);
        }
        if (loc.contacts) {
          loc.contacts.forEach(contact => {
            result.push({
              id: loc.locationId,
              auctionName: loc.auctionName,
              ...contact,
            });
          });
        }
      });
    }
    setContacts(result);
  }, []);

  useEffect(() => {
    if (error) {
      const errorMsg = Array.isArray(error)
        ? error.map(e => e.message).join(', ')
        : error.message;
      setErrorMessage(errorMsg);
    } else {
      setErrorMessage(null);
    }
  }, [error]);

  useEffect(() => {
    if (!paramLocationId) {
      history.push('/auction_location');
    } else {
      setLocationId(+paramLocationId);
    }
  }, [paramLocationId, history]);

  useEffect(() => {
    if (contactsData && contactsData.apmsLocationContactsList) {
      processedData(contactsData.apmsLocationContactsList);
    }
  }, [contactsData, processedData]);

  const handleExportCSV = async() => {
    if (isExporting) return; // Prevent multiple clicks
    setIsExporting(true);
    try {
      const apmsLocationContactsList = contactsData?.apmsLocationContactsList;
      const auction = Array.isArray(apmsLocationContactsList) && apmsLocationContactsList.length > 0
        ? apmsLocationContactsList[0]
        : null;
      const list = Array.isArray(auction?.contacts) ? auction.contacts : [];
      list.forEach(contact => {
        contact.auctionName = auction.auctionName;
      });
      setCsvData(auctionContactCSV(list));
      setCsvReady(true);

      // wait for state updated before trigger download
      setTimeout(() => {
        csvLinkRef.current.link.click();
        setCsvReady(false);
        setIsExporting(false);
      }, 0);
    } catch (err) {
      setIsExporting(false);
    }
  };

  return (
    <div style={{ paddingBottom: '4em' }}>
      {flashMessage && (
        <Message
          onDismiss={() => {
            setFlashMessage('');
            setSuccessMessage(false);
          }}
          content={flashMessage}
          positive={!isNegativeFlashMsg}
          negative={isNegativeFlashMsg}
        />
      )}
      {contactLoading && (
        <Dimmer inverted active>
          <Loader indeterminate>Loading...</Loader>
        </Dimmer>
      )}
      {errorMessage && (
        <Message negative>
          <p>{errorMessage}</p>
        </Message>
      )}
      <div className='d-flex-between'>
        <Header>{`Auction Contacts for Dealers ${locationName}`}</Header>
        <div className='button-max-content' style={{ display: 'flex', gap: '0.5em' }}>
          <Button
            basic
            icon='file excel'
            content={isExporting ? 'Exporting...' : 'Export to CSV'}
            onClick={handleExportCSV}
            disabled={isExporting}
          />
          { csvReady && (
            <CSVLink
              data={csvData}
              headers={auctionContactHeader}
              filename={exportFileName}
              enclosingCharacter='"'
              ref={csvLinkRef}
              style={{ display: 'none' }}
            />
          )}
          <Button disabled={!isAuctionLocationEditor} color='green' inverted onClick={handleCreateNewContact}>
            Create New Contact
          </Button>
        </div>
      </div>
      <ContactList
        hasLocationName={false}
        auctionContactList={contacts}
        onSelectContact={handleSelectContact}
        isAuctionLocationEditor={isAuctionLocationEditor}
      />

      <AuctionContactForm
        isModalOpen={showLocationModal}
        title={`${contactData ? 'Edit' : 'Create'} Auction Contact`}
        isLoading={loading}
        onSubmitHandler={onFormSubmitHandler}
        onCloseHandler={onCloseHandler}
        locationId={locationId}
        errorMessage={modalError}
        successMessage={successMessage}
        onClearError={onClearError}
        contactData={contactData}
      />
    </div>
  );
};

AuctionContacts.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      locationId: PropTypes.string,
    }),
  }).isRequired,
  createOrUpdateLocationContact: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default compose(
  withRouter,
  LoadingDataEnhancer,
  graphql(createOrUpdateLocationContactMutation, {
    name: 'createOrUpdateLocationContact',
  })
)(AuctionContacts);
